"use client";

import { GameEmbedView } from "@/components/feature/game-embed-view";
import { GameHeader } from "@/components/feature/game-header";
import { GameMetadataOverlay } from "@/components/feature/game-metadata-overlay";
import { VideoPlayer } from "@/components/feature/video-player";
import type { VideoPlayerRef } from "@/components/feature/video-player";
import { FullscreenProvider } from "@/contexts/fullscreen-context";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";
import { useClearDailyPuzzleReturnOnLeave } from "@/hooks/useClearDailyPuzzleReturnOnLeave";
import { useDailyPuzzlePlayTimer } from "@/hooks/useDailyPuzzlePlayTimer";
import { useGameEngagementTimer } from "@/hooks/useGameEngagementTimer";
import { useGame } from "@/hooks/useGames";
import {
  GAME_THEATER_CHROME_SIDE,
  getGameTheaterChromeBarClass,
  getGameTheaterStageClass,
} from "@/lib/constants/game-theater-chrome";
import {
  GAME_THEATER_PLAYER_BACKGROUND,
  GAME_THEATER_PLAYER_BG_CLASS,
  GAME_THEATER_STAGE_HEIGHT_FULL,
} from "@/lib/constants/layout";
import { Z_INDEX_CLASSES } from "@/lib/constants/z-index";
import { DAILY_PUZZLE_RETURN_PATH, consumeDailyPuzzleReturn } from "@/lib/daily-puzzle/return-path";
import { beginNpsGameSession } from "@/lib/nps/session";
import {
  type PageOriginContext,
  appendPageOriginToUrl,
  readPageOrigin,
  resolveGameReturnHref,
} from "@/lib/page-origin-context";
import { absoluteUrl } from "@/lib/seo/site";
import { cn } from "@/lib/utils";
import { UTM_CAMPAIGN_GAME_SHARE, UTM_SITE_SHARE_DEFAULTS, appendUtmParams } from "@/lib/utm";
import type { GameWithRelations } from "@/types/game";
import { XIcon } from "@phosphor-icons/react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { usePostHog } from "posthog-js/react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

export type GameExperienceMode = "overlay" | "page";

export interface GameExperienceProps {
  slug: string;
  mode: GameExperienceMode;
  pageOrigin?: PageOriginContext | null;
}

const SKELETON_PULSE = "animate-pulse bg-white/20";

function GameTheaterPlayerSkeleton() {
  return (
    <div
      className={`relative h-full w-full overflow-hidden ${GAME_THEATER_PLAYER_BG_CLASS}`}
      style={{ backgroundColor: GAME_THEATER_PLAYER_BACKGROUND }}
      data-testid="game-theater-player-skeleton"
      aria-hidden
    >
      <div className="absolute inset-0 animate-pulse bg-neutral-900" />
    </div>
  );
}

function GameTheaterHeaderSkeleton() {
  return (
    <div
      className="flex h-full w-auto flex-col items-center justify-between gap-3 overflow-visible max-md:portrait:!h-auto max-md:portrait:!w-full max-md:portrait:!flex-row"
      aria-hidden
    >
      <div
        className={`flex shrink-0 flex-col items-center gap-3 max-md:portrait:!flex-row ${SKELETON_PULSE}`}
      >
        <div className="h-10 w-10 shrink-0 rounded-full md:h-12 md:w-12" />
        <div className="h-10 w-10 shrink-0 rounded-lg md:h-12 md:w-12" />
      </div>
      <div
        className={`flex shrink-0 flex-col-reverse items-center gap-3 max-md:portrait:!flex-row ${SKELETON_PULSE}`}
      >
        <div className="hidden h-12 w-12 rounded-full md:block" />
        <div className="h-10 w-10 rounded-full md:h-12 md:w-12 max-md:portrait:h-12 max-md:portrait:w-24" />
      </div>
    </div>
  );
}

function GameExperienceLoader() {
  return (
    <main id="main-content" className="flex-grow text-white">
      <GameTheaterStage
        player={<GameTheaterPlayerSkeleton />}
        header={<GameTheaterHeaderSkeleton />}
      />
    </main>
  );
}

function GameExperienceNotFound() {
  return (
    <main className="flex-grow text-white flex items-center justify-center px-4 py-16">
      <div className="max-w-md text-center">
        <h1 className="charlie-kingdom-font text-4xl md:text-5xl mb-6">Game Not Found</h1>
        <p className="text-xl mb-8">
          Sorry, we couldn&apos;t find the game you&apos;re looking for.
        </p>
        <Link
          href="/games"
          className="inline-block bg-white text-brand-indigo px-6 py-3 rounded-full font-medium hover:bg-gray-100 transition-colors"
        >
          Browse All Games
        </Link>
      </div>
    </main>
  );
}

interface GameTheaterStageProps {
  player: ReactNode;
  header: ReactNode;
}

function GameTheaterStage({ player, header }: GameTheaterStageProps) {
  return (
    <section
      className={getGameTheaterStageClass()}
      style={{ height: GAME_THEATER_STAGE_HEIGHT_FULL }}
      aria-label="Game player"
    >
      <div
        data-testid="game-theater-player-slot"
        className={`relative z-0 min-h-0 min-w-0 flex-1 ${GAME_THEATER_PLAYER_BG_CLASS}`}
        style={{ backgroundColor: GAME_THEATER_PLAYER_BACKGROUND }}
      >
        {player}
      </div>
      <div
        data-testid="game-theater-chrome"
        data-chrome-side={GAME_THEATER_CHROME_SIDE}
        className={cn(Z_INDEX_CLASSES.GAME_THEATER_HEADER, getGameTheaterChromeBarClass())}
      >
        {header}
      </div>
    </section>
  );
}

interface GameContentProps {
  game: GameWithRelations;
  hasVideo: boolean;
  isShowingVideo: boolean;
  videoPlayerRef: React.RefObject<VideoPlayerRef>;
}

function GameContent({ game, hasVideo, isShowingVideo, videoPlayerRef }: GameContentProps) {
  const hasGameContent = game.url || game.source_type === "asset";

  if (!hasGameContent) {
    return (
      <div className="flex h-full items-center justify-center p-8 text-center">
        <p className="text-white/70">Game content is not available yet. Check back soon!</p>
      </div>
    );
  }

  return (
    <div
      className={`relative h-full w-full ${GAME_THEATER_PLAYER_BG_CLASS}`}
      style={{ backgroundColor: GAME_THEATER_PLAYER_BACKGROUND }}
    >
      <div
        className={`relative h-full ${GAME_THEATER_PLAYER_BG_CLASS} ${isShowingVideo && hasVideo ? "hidden" : ""}`}
        style={{ backgroundColor: GAME_THEATER_PLAYER_BACKGROUND }}
      >
        <GameEmbedView game={game} withFullscreenProvider={false} fillContainer />
      </div>

      {isShowingVideo && hasVideo && game.tutorial_video_url && (
        <VideoPlayer
          videoUrl={game.tutorial_video_url}
          title={`${game.name} Tutorial`}
          ref={videoPlayerRef}
          fillContainer
        />
      )}
    </div>
  );
}

interface GameExperienceContentProps {
  game: GameWithRelations;
  onClose: () => void;
  pageOrigin?: PageOriginContext | null;
}

function GameExperienceContent({ game, onClose, pageOrigin }: GameExperienceContentProps) {
  const [isShowingVideo, setIsShowingVideo] = useState(false);
  const [isMetadataOpen, setIsMetadataOpen] = useState(false);
  const videoPlayerRef = useRef<VideoPlayerRef>(null);

  useGameEngagementTimer(game.slug);
  useDailyPuzzlePlayTimer(game.slug);

  useEffect(() => {
    beginNpsGameSession({ slug: game.slug, id: game.id });
  }, [game.slug, game.id]);

  const hasVideo = !!(game.enable_tutorial_video && game.tutorial_video_url);
  const hasMetadata = !!(game.seo_content || game.publisher);
  const hasGameContent = Boolean(game.url || game.source_type === "asset");

  const toggleVideo = useCallback(() => {
    setIsShowingVideo((prev) => !prev);
  }, []);

  useEffect(() => {
    setIsShowingVideo(false);
    setIsMetadataOpen(false);
  }, [game.slug]);

  const gameHeader = hasGameContent ? (
    <GameHeader
      title={game.name}
      iconUrl={game.thumbnail_url}
      hasVideo={hasVideo}
      isShowingVideo={isShowingVideo}
      onVideoToggle={toggleVideo}
      hasMetadata={hasMetadata}
      onOpenMetadata={hasMetadata ? () => setIsMetadataOpen(true) : undefined}
      shareUrl={appendPageOriginToUrl(
        appendUtmParams(absoluteUrl(`/games/${game.slug}`), {
          ...UTM_SITE_SHARE_DEFAULTS,
          utm_campaign: UTM_CAMPAIGN_GAME_SHARE,
        }),
        pageOrigin
      )}
      shareTitle={game.name}
      onClose={onClose}
    />
  ) : (
    <div className="flex items-center gap-4 py-3">
      <button
        type="button"
        onClick={onClose}
        className="flex h-10 w-10 md:h-12 md:w-12 shrink-0 items-center justify-center rounded-full bg-brand-purple hover:bg-brand-purple-dark text-white transition-colors"
        aria-label="Close"
        title="Close"
      >
        <XIcon weight="bold" size={20} />
      </button>
      {game.thumbnail_url && (
        <img
          src={game.thumbnail_url}
          alt={`${game.name} icon`}
          className="w-10 h-10 md:w-12 md:h-12 rounded-lg object-cover flex-shrink-0"
        />
      )}
      <h1 className="hidden md:block charlie-kingdom-font text-3xl md:text-4xl text-white font-bold">
        {game.name}
      </h1>
    </div>
  );

  return (
    <FullscreenProvider>
      <div className="relative flex h-full min-h-0 flex-1 flex-col">
        <main id="main-content" className="flex h-full min-h-0 flex-col text-white">
          {hasGameContent ? (
            <GameTheaterStage
              player={
                <GameContent
                  game={game}
                  hasVideo={hasVideo}
                  isShowingVideo={isShowingVideo}
                  videoPlayerRef={videoPlayerRef as React.RefObject<VideoPlayerRef>}
                />
              }
              header={gameHeader}
            />
          ) : (
            <div className="container mx-auto max-w-[1024px] px-3 sm:px-4">{gameHeader}</div>
          )}
        </main>

        {hasMetadata && (
          <GameMetadataOverlay game={game} open={isMetadataOpen} onOpenChange={setIsMetadataOpen} />
        )}
      </div>
    </FullscreenProvider>
  );
}

export function GameExperience({ slug, mode, pageOrigin }: GameExperienceProps) {
  const router = useRouter();
  const posthog = usePostHog();
  const lastCapturedNotFoundSlugRef = useRef<string | null>(null);
  const { lock: lockBodyScroll, unlock: unlockBodyScroll } = useBodyScrollLock();

  const { data: game, isLoading: gameLoading, error: gameError } = useGame(slug);

  const isNotFoundState = ((!game && !gameLoading) || gameError) && Boolean(slug);

  // Internal navigation uses clean URLs (no origin params), carrying the came-from origin in
  // sessionStorage. When the server didn't resolve an origin from a shared `from=` link, recover
  // it client-side so the back/close target returns the player to where they came from.
  const [clientOrigin, setClientOrigin] = useState<PageOriginContext | null>(null);

  useEffect(() => {
    setClientOrigin(pageOrigin ? null : readPageOrigin());
  }, [pageOrigin, slug]);

  const effectiveOrigin = pageOrigin ?? clientOrigin;
  useClearDailyPuzzleReturnOnLeave(slug);

  const handleClose = useCallback(() => {
    if (mode === "overlay") {
      consumeDailyPuzzleReturn(slug);
      router.back();
      return;
    }
    if (consumeDailyPuzzleReturn(slug)) {
      router.push(DAILY_PUZZLE_RETURN_PATH);
      return;
    }
    router.push(resolveGameReturnHref(effectiveOrigin));
  }, [mode, effectiveOrigin, router, slug]);

  useEffect(() => {
    if (isNotFoundState) {
      return;
    }
    lockBodyScroll();
    return unlockBodyScroll;
  }, [isNotFoundState, lockBodyScroll, unlockBodyScroll]);

  useEffect(() => {
    if (!isNotFoundState) return;
    if (!posthog?.capture) return;
    if (lastCapturedNotFoundSlugRef.current === slug) return;

    lastCapturedNotFoundSlugRef.current = slug;

    posthog.capture("game_not_found", {
      game_slug: slug,
      path: window.location.pathname,
      search: window.location.search,
      referrer: document.referrer || null,
      source: mode === "overlay" ? "game-overlay" : "game-page",
      is_error_state: Boolean(gameError),
    });
  }, [gameError, isNotFoundState, mode, posthog, slug]);

  if (gameLoading && !game) {
    return <GameExperienceLoader />;
  }

  if (!game || gameError) {
    return <GameExperienceNotFound />;
  }

  return <GameExperienceContent game={game} onClose={handleClose} pageOrigin={effectiveOrigin} />;
}
